import * as React from 'react'

import { Avatar, Box, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/PopoverMenu'
import SelectField from 'modules/core/components/SelectField'
import { track } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import type User from 'modules/core/store/models/User'

import { events } from 'modules/reports/lib/events'
import {
  ReportShareMode,
  ReportShareVisibilities,
  ReportShareVisibility,
} from 'modules/reports/store/models/ReportShare'

interface Props {
  user: User
  mode: ReportShareMode
  visibility: ReportShareVisibility
  onVisibilityChange: (visibility: ReportShareVisibility) => void
  onDelete: () => void
}

const displayedShareVisibilities = [ReportShareVisibilities.Viewer, ReportShareVisibilities.Editor]

function Sharee({ user, visibility, onVisibilityChange, onDelete }: Props) {
  const avatarUrl = user.attributes.avatar
  const name = user.fullName()
  const onDeleteClick = () => {
    track(events.ReportShareDeleteSelected)
    onDelete()
  }
  const options = displayedShareVisibilities.map((visibilityOption) => (
    <Popover.TouchableOption
      key={visibilityOption}
      title={visibilityLabel(visibilityOption)}
      description={visibilityDescription(visibilityOption)}
      onClick={() => {
        track(events.ReportShareVisibilitySelected)
        onVisibilityChange(visibilityOption)
      }}
    />
  ))

  options.push(
    <Popover.TouchableOption
      key="delete"
      title={i18n.t('custom_reports.share.modal.share_options.remove_access.title')}
      description={i18n.t('custom_reports.share.modal.share_options.remove_access.description')}
      danger
      onClick={onDeleteClick}
    />
  )

  return (
    <Box flexDirection="row" alignItems="center" justifyContent="flexStart">
      <Avatar size="s32" url={buildUrl(avatarUrl)} label={name} />
      <Box minWidth="none" paddingLeft="s8" paddingRight="s16" flexGrow>
        <Text size="300" weight="medium" color="grey1000" ellipsis>
          {name}
        </Text>
      </Box>
      <Box width="s140" flexGrow={false} flexNoShrink maxWidth="s140">
        <Popover type="list">
          <Popover.Trigger>
            <SelectField
              options={[{ label: visibilityLabel(visibility), value: visibility }]}
              value={visibility}
            />
          </Popover.Trigger>
          <Popover.Content side="bottom" align="end">
            {options}
          </Popover.Content>
        </Popover>
      </Box>
    </Box>
  )
}

function visibilityLabel(visibility: ReportShareVisibility): string {
  switch (visibility) {
    case ReportShareVisibilities.Viewer:
      return i18n.t('custom_reports.share.modes.viewer')
    case ReportShareVisibilities.Editor:
      return i18n.t('custom_reports.share.modes.editor')
    default:
      throw Error(`Unknown report share visibility: ${visibility}`)
  }
}

function visibilityDescription(visibility: ReportShareVisibility): string {
  switch (visibility) {
    case ReportShareVisibilities.Viewer:
      return i18n.t('custom_reports.share.modal.share_options.viewer.description')
    case ReportShareVisibilities.Editor:
      return i18n.t('custom_reports.share.modal.share_options.editor.description')
    default:
      throw Error(`Unknown report share visibility: ${visibility}`)
  }
}

export default observer(Sharee)
